// AskCalls — the "Ask the calls" box on the TickerSheet Earnings tab.
//
// One question in, one answer out, cited. POSTs to /api/ask-calls with the
// Clerk session token (the whole app sits under <SignedIn>, so a token always
// exists); the server embeds the question, finds the closest passages in
// THIS ticker's calls, has Claude answer from those passages only, and
// returns citations it has already validated against what it sent. Tapping a
// citation chip hands { fiscal_year, fiscal_quarter, n_start } to the parent,
// which opens that quarter and scrolls to the paragraph.
//
// Deliberately renders no passage text of its own: the paragraph the chip
// jumps to is rendered by QuarterDetail under the existing
// VITE_SHOW_TRANSCRIPT_BODY gate, so the legal switch stays in one place.
//
// Styling: the Search.jsx input row (studio tokens, Mono) and the
// FeedbackWidget state machine (idle | sending | done | error).

import { useState, useRef } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { Chip } from './lib'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const MAX_CHARS = 300

export default function AskCalls({ ticker, onCite }) {
  const { getToken } = useAuth()
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('idle')
  const [res, setRes] = useState(null)
  const [errMsg, setErrMsg] = useState('')
  const inputRef = useRef(null)

  const canSend = q.trim().length >= 3 && status !== 'sending'

  async function submit() {
    if (!canSend) return
    const question = q.trim()
    setStatus('sending'); setRes(null); setErrMsg('')
    try {
      const token = await getToken()
      const r = await fetch('/api/ask-calls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ ticker, question }),
        signal: AbortSignal.timeout(75000),
      })
      const body = await r.json().catch(() => ({}))
      if (!r.ok) throw Object.assign(new Error(body?.error || `HTTP ${r.status}`), { status: r.status })
      setRes(body)
      setStatus('done')
    } catch (e) {
      // 4xx messages are for the user (sign in, too short, rate limit). A 5xx
      // body is vendor plumbing — e.g. "openrouter 402: Insufficient credits" —
      // which must never reach the screen, so show a generic line instead.
      const s = e?.status
      setErrMsg(s && s < 500 ? String(e.message).slice(0, 160) : 'Try again in a little while.')
      setStatus('error')
    }
  }

  const label = (c) => `Q${c.fiscal_quarter} FY${c.fiscal_year} ¶${c.n_start}`

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.14em', color: 'hsl(var(--studio-text-subtle))', marginBottom: 8 }}>
        ASK THE CALLS
      </div>

      <form onSubmit={(e) => { e.preventDefault(); submit() }}
        style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 9, padding: '9px 12px',
          background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))',
          borderRadius: 'var(--studio-radius)' }}>
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value.slice(0, MAX_CHARS))}
            placeholder="What has management said about…"
            aria-label={`Ask a question about ${ticker}'s earnings calls`}
            enterKeyHint="send"
            style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', outline: 'none',
              color: 'hsl(var(--studio-text))', fontSize: 13, lineHeight: 1.4 }}
          />
          {q && status !== 'sending' && (
            <button type="button" onClick={() => { setQ(''); setRes(null); setStatus('idle'); inputRef.current?.focus() }}
              aria-label="Clear question"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--studio-text-subtle))', ...Mono, fontSize: 11, padding: 0 }}>✕</button>
          )}
        </div>
        <button type="submit" disabled={!canSend}
          style={{ ...Mono, fontSize: 10.5, letterSpacing: '0.06em', background: 'none', border: 'none',
            cursor: canSend ? 'pointer' : 'default', padding: '6px 2px',
            color: canSend ? 'hsl(var(--studio-accent-soft))' : 'hsl(var(--studio-text-subtle))' }}>
          {status === 'sending' ? '…' : 'ASK'}
        </button>
      </form>

      {status === 'sending' && (
        <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', marginTop: 10 }}>
          SEARCHING {ticker}'S CALLS…
        </div>
      )}

      {status === 'error' && (
        <div style={{ marginTop: 10 }}>
          <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-danger))' }}>ANSWERING UNAVAILABLE</div>
          {errMsg && <div style={{ fontSize: 11, color: 'hsl(var(--studio-text-subtle))', marginTop: 4 }}>{errMsg}</div>}
        </div>
      )}

      {status === 'done' && res && (
        <div style={{ marginTop: 10 }}>
          {res.answer ? (
            <div style={{ fontSize: 12.5, lineHeight: 1.65, color: 'hsl(var(--studio-text))' }}>{res.answer}</div>
          ) : null}
          {res.insufficient_evidence && (
            <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', marginTop: res.answer ? 8 : 0 }}>
              NOT ADDRESSED ON THESE CALLS
            </div>
          )}
          {res.citations?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginTop: 8, marginLeft: -6 }}>
              {res.citations.map((c) => (
                <Chip key={`${c.fiscal_year}-${c.fiscal_quarter}-${c.n_start}-${c.n_end}`} onClick={() => onCite?.(c)}>
                  {label(c)}
                </Chip>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
